import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { A11y, Mousewheel, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Flex, Heading, IconButton, Text, useBreakpointValue } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { useCatalogItems } from '~/features/catalog/hooks/use-catalog-items';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import {
  popularCatalogItems,
  topAiSkillsCatalogItems,
} from '~/features/page-template/templates/home/model/recommendations';
import { useProgramRecommendations } from '~/features/program/hooks/use-program-recommendations';
import { useUser } from '~/features/user/hooks/use-user';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import { useExperimentalSegmentEvents } from '../hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';

import { CatalogCard, CatalogCardSkeleton } from './catalog-card';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';

export type ExperienceSelectedProgramsSectionProps = StaticExperienceSelectedProgramsSectionProps & {
  mostPopularCatalogItems?: CatalogItem[];
};

export function ExperienceSelectedProgramsSection({
  title,
  description,
  newPrograms,
  mostPopularCatalogItems,
}: ExperienceSelectedProgramsSectionProps) {
  const { t } = useI18n();
  const [isPersonalRecommendationsRendered, setIsPersonalRecommendationsRendered] = useState(false);

  return (
    <Flex
      as='section'
      flexDirection='column'
      gap={{
        base: '40px',
        md: '80px',
      }}
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer>
        <Box margin='0 auto' maxWidth='858px' textAlign='center' width='100%'>
          <Heading
            as='h2'
            fontWeight='600'
            lineHeight='1.1'
            mb={6}
            fontSize={{
              base: '2.5rem',
              md: '3.375rem',
            }}
          >
            {title}
          </Heading>
          <Text color='grey.500'>{description}</Text>
        </Box>
      </ContentContainer>
      <PersonalizedProgramCollectionContainer
        setIsPersonalRecommendationsRendered={setIsPersonalRecommendationsRendered}
      />
      <ProgramCollection
        catalogItems={mostPopularCatalogItems ?? popularCatalogItems}
        title={t('home.mostPopularCourses')}
      />
      <ProgramCollection catalogItems={newPrograms} title={t('home.newlyReleasedCourses')} />
      {!isPersonalRecommendationsRendered && (
        <ProgramCollection catalogItems={topAiSkillsCatalogItems} title={t('home.topAiDataSkillsCourses')} />
      )}
    </Flex>
  );
}

type ProgramCollectionProps = {
  title: string;
  catalogItems: CatalogItem[];
  isLoading?: boolean;
};
function ProgramCollection({ title, catalogItems, isLoading = false }: ProgramCollectionProps) {
  return (
    <Box>
      <Heading
        alignItems='center'
        as='h3'
        display='flex'
        fontWeight='600'
        gap={3}
        lineHeight='1.1'
        maxWidth='1056px'
        _after={{
          content: '""',
          display: 'block',
          height: '1px',
          backgroundColor: 'grey.300',
          flex: 1,
        }}
        fontSize={{
          base: '1rem',
          md: '1.5rem',
        }}
        margin={{
          base: '0px 16px 24px',
          md: '0px 24px 24px',
          xl: '0 auto 24px',
        }}
      >
        {title}
      </Heading>
      <ProgramCarousel catalogItems={catalogItems} isLoading={isLoading} title={title} />
    </Box>
  );
}
type ProgramCarouselProps = {
  title: string;
  catalogItems: CatalogItem[];
  isLoading?: boolean;
};
function ProgramCarousel({ catalogItems, title, isLoading = false }: ProgramCarouselProps) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { i18n, isRtl } = useI18n();
  const isClientReady = useClientRender();
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: 'md' }
  );
  /**
   * SSR uses Chakra's `md` fallback (desktop). Lock pre-client slide metrics to that so the static
   * row HTML matches the server; real `isMobile` applies once Swiper mounts after hydration.
   */
  const carouselIsMobile = !isClientReady ? false : isMobile ?? false;
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const handleSlideMove = debounce(() => {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'ExperiencePage CTA Clicked',
        type: 'click',
        label: 'course_scroll_clicked',
      });
    }
  }, 100);

  return (
    <Flex alignItems='center' flexDirection='row' gap={6} margin='0 auto' maxWidth='1240px' position='relative'>
      <Box
        isolation='isolate'
        margin='0 auto'
        maxWidth='1096px'
        width='100%'
        sx={
          isClientReady
            ? {
                '.swiper .swiper-slide': {
                  height: 'auto',
                  display: 'flex',
                },
              }
            : undefined
        }
      >
        {!isClientReady ? (
          <Flex
            gap={carouselIsMobile ? 4 : 6}
            overflowX='auto'
            overflowY='hidden'
            sx={{ WebkitOverflowScrolling: 'touch' }}
            width='100%'
          >
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <Box key={`skeleton-${index}`} flexShrink={0} width={carouselIsMobile ? '170px' : '256px'}>
                    <CatalogCardSkeleton type='catalog' width='100%' />
                  </Box>
                ))
              : catalogItems.map((catalogItem) => (
                  <Box key={catalogItem.key} flexShrink={0} width={carouselIsMobile ? '170px' : '256px'}>
                    <CatalogCard
                      catalogItem={catalogItem}
                      width='100%'
                      onClick={() =>
                        trackEvent({
                          name: 'ExperiencePage CTA Clicked',
                          type: 'click',
                          label: title,
                          value: catalogItem.title ?? '',
                          additionalAttributes: {
                            product_ids: [catalogItem.key],
                          },
                        })
                      }
                    />
                  </Box>
                ))}
          </Flex>
        ) : (
          <Swiper
            key={isMobile ? 'mobile' : 'desktop'}
            centeredSlides={isMobile}
            direction='horizontal'
            initialSlide={isMobile ? 0 : undefined}
            loop={isMobile}
            modules={[A11y, Mousewheel, Navigation]}
            slidesPerView='auto'
            spaceBetween={isMobile ? 16 : 24}
            breakpoints={{
              [768]: {
                slidesOffsetBefore: 24,
              },
              [1080]: {
                slidesOffsetBefore: 0,
              },
            }}
            mousewheel={{
              forceToAxis: true,
              thresholdDelta: 10,
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.changeLanguageDirection(i18n.dir());
            }}
            onNavigationNext={() => {
              handleSlideMove();
            }}
            onNavigationPrev={() => {
              handleSlideMove();
            }}
            onSliderMove={() => {
              handleSlideMove();
            }}
          >
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <SwiperSlide
                    key={`skeleton-${index}`}
                    style={{
                      width: isMobile ? '170px' : '256px',
                    }}
                  >
                    <CatalogCardSkeleton type='catalog' width='100%' />
                  </SwiperSlide>
                ))
              : catalogItems.map((catalogItem) => (
                  <SwiperSlide
                    key={catalogItem.key}
                    style={{
                      width: isMobile ? '170px' : '256px',
                    }}
                  >
                    <CatalogCard
                      catalogItem={catalogItem}
                      width='100%'
                      onClick={() =>
                        trackEvent({
                          name: 'ExperiencePage CTA Clicked',
                          type: 'click',
                          label: title,
                          value: catalogItem.title ?? '',
                          additionalAttributes: {
                            product_ids: [catalogItem.key],
                          },
                        })
                      }
                    />
                  </SwiperSlide>
                ))}
          </Swiper>
        )}
      </Box>
      <IconButton
        ref={prevRef}
        aria-label='Previous'
        left={0}
        position='absolute'
        size='lg'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'block',
        }}
        icon={
          isRtl ? (
            <ChevronRightIcon height='48px' opacity={0.5} width='48px' />
          ) : (
            <ChevronLeftIcon height='48px' opacity={0.5} width='48px' />
          )
        }
      />
      <IconButton
        ref={nextRef}
        aria-label='Next'
        position='absolute'
        right={0}
        size='lg'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'block',
        }}
        icon={
          isRtl ? (
            <ChevronLeftIcon height='48px' opacity={0.5} width='48px' />
          ) : (
            <ChevronRightIcon height='48px' opacity={0.5} width='48px' />
          )
        }
      />
    </Flex>
  );
}

function PersonalizedProgramCollectionContainer({
  setIsPersonalRecommendationsRendered,
}: {
  setIsPersonalRecommendationsRendered: (isPersonalRecommendationsRendered: boolean) => void;
}) {
  const isClient = useClientRender();

  const { isUserAuthenticated } = useUser();
  const isSubscribedUser = isClient ? getActiveSubscriptionCookie() : undefined;

  const isAuthenticatedUserAndNotSubscribed = isUserAuthenticated && isSubscribedUser === false;

  useEffect(() => {
    setIsPersonalRecommendationsRendered(isAuthenticatedUserAndNotSubscribed);
  }, [isAuthenticatedUserAndNotSubscribed, setIsPersonalRecommendationsRendered]);

  if (!isAuthenticatedUserAndNotSubscribed) return null;

  return <PersonalizedProgramCollection setIsPersonalRecommendationsRendered={setIsPersonalRecommendationsRendered} />;
}

function PersonalizedProgramCollection({
  setIsPersonalRecommendationsRendered,
}: {
  setIsPersonalRecommendationsRendered: (isPersonalRecommendationsRendered: boolean) => void;
}) {
  const { t } = useI18n();
  const { programRecommendations, programRecommendationsIsLoading, programRecommendationsIsFetched } =
    useProgramRecommendations();
  const { catalogItems, catalogItemsIsLoading } = useCatalogItems(programRecommendations ?? []);
  const isLoading = programRecommendationsIsLoading || catalogItemsIsLoading;
  const hasPersonalRecommendations = !!programRecommendations?.length;
  const hidePersonalRecommendations = programRecommendationsIsFetched && !hasPersonalRecommendations;

  useEffect(() => {
    if (hidePersonalRecommendations) {
      setIsPersonalRecommendationsRendered(false);
    }
  }, [hidePersonalRecommendations, setIsPersonalRecommendationsRendered]);

  if (hidePersonalRecommendations) return null;

  return (
    <ProgramCollection
      catalogItems={catalogItems || []}
      isLoading={isLoading}
      title={t('dashboard.recommendedForYou')}
    />
  );
}

export async function transformStaticExperienceSelectedProgramsSectionProps() {
  const { getNewProgramsList } = await import('~/features/program/controllers/get-new-programs-list');
  const { getCatalogItemsFromServer } = await import('~/features/catalog/controllers/get-catalog-items-from-server');

  const newProgramKeys = await getNewProgramsList();
  const newPrograms = getCatalogItemsFromServer(newProgramKeys ?? []);
  return {
    title: 'Master the skills that matter most',
    description:
      'Join 25M+ learners mastering AI, Data, Programming, and more. Learn on your schedule and move your career forward. Build the skills top companies hire for, and put them to work!',
    newPrograms,
  };
}

export type StaticExperienceSelectedProgramsSectionProps = Awaited<
  ReturnType<typeof transformStaticExperienceSelectedProgramsSectionProps>
>;
